/**
 * T17 (Nivo 4, spec §4.6) — the five calls of
 * `/api/v2/organizations/:organizationId/finance-entries`.
 *
 * All of them are ORGANIZATION-SCOPED by path, which is what makes the shared
 * `SecurityGuard` narrow the actor's permissions to the roles held in that one
 * organization. Nothing here ever puts an organization or tenant id in a body
 * or a query string: the DTOs are `.strict()` and would answer 422.
 *
 * The envelopes are kept exactly as the backend sends them — the list page
 * carries `page`/`pageSize`/`total`/`currentMonth` beside its rows, and a
 * monthly PATCH can answer with a `successor` segment. Unwrapping either here
 * would throw away the only information the UI has for explaining WHY a change
 * landed where it did. `amountMinor` strings are never parsed.
 */
import type {
  CreateFinanceEntryArgs,
  DeleteFinanceEntryArgs,
  FetchFinanceCategoryTotalsArgs,
  FetchFinanceEntriesArgs,
  FetchFinanceNetResultArgs,
  FinanceCategoryTotalsResponse,
  FinanceEntriesPage,
  FinanceEntryMutationResult,
  FinanceEntryRow,
  FinanceNetResultResponse,
  UpdateFinanceEntryArgs,
} from './types/financeEntries.types';
import type { BaseApiResponse } from './types/api.types';

import { destroy, patch, post, get } from './axios';

const entriesUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/finance-entries`;

export const fetchFinanceEntries = async ({
  filters,
  organizationId,
  page,
  pageSize,
}: FetchFinanceEntriesArgs): Promise<FinanceEntriesPage | null> => {
  const response = await get<FinanceEntriesPage>(entriesUrl(organizationId), {
    query: {
      ...filters,
      page: String(page),
      pageSize: String(pageSize),
    },
  });
  return response ?? null;
};

export const fetchFinanceNetResult = async ({
  from,
  organizationId,
  to,
}: FetchFinanceNetResultArgs): Promise<FinanceNetResultResponse | null> => {
  // `tz` used to travel here too; the server now cuts the ledger in the
  // ORGANIZATION's own zone (Foundation 1, D6/D7), so nothing is sent for it.
  const response = await get<FinanceNetResultResponse>(
    `${entriesUrl(organizationId)}/net-result`,
    { query: { from, to } },
  );
  return response ?? null;
};

/**
 * Task 5.2 (design 2c) — income and expenses by sub-section plus the
 * read-only automatic rows of the entry list, for the same months as
 * `fetchFinanceNetResult`. Every currency comes back; the page's selector only
 * decides which is drawn.
 */
export const fetchFinanceCategoryTotals = async ({
  from,
  organizationId,
  to,
}: FetchFinanceCategoryTotalsArgs): Promise<FinanceCategoryTotalsResponse | null> => {
  const response = await get<FinanceCategoryTotalsResponse>(
    `${entriesUrl(organizationId)}/category-totals`,
    { query: { from, to } },
  );
  return response ?? null;
};

export const createFinanceEntry = async ({
  dto,
  organizationId,
}: CreateFinanceEntryArgs): Promise<FinanceEntryRow | null> => {
  const response = await post<FinanceEntryRow>(entriesUrl(organizationId), dto);
  return response?.data ?? null;
};

/**
 * A monthly `edit` of a segment that started in a CLOSED month cannot touch
 * that history, so the server closes it and opens a successor in the same
 * transaction. Both rows come back; the caller refetches the series either way,
 * and `successor` is what lets it say which months moved.
 */
export const updateFinanceEntry = async ({
  dto,
  financeEntryId,
  organizationId,
}: UpdateFinanceEntryArgs): Promise<FinanceEntryMutationResult | null> => {
  const response = (await patch<FinanceEntryRow>(
    `${entriesUrl(organizationId)}/${financeEntryId}`,
    dto,
  )) as
    | (BaseApiResponse<FinanceEntryRow> & { successor?: FinanceEntryRow })
    | undefined;
  if (!response?.data) return null;
  return { data: response.data, successor: response.successor };
};

export const deleteFinanceEntry = async ({
  financeEntryId,
  organizationId,
  scope,
  version,
}: DeleteFinanceEntryArgs): Promise<boolean> => {
  const query = new URLSearchParams({ version: String(version) });
  if (scope) query.set('scope', scope);
  await destroy(
    `${entriesUrl(organizationId)}/${financeEntryId}?${query.toString()}`,
  );
  return true;
};
